const clone = value => JSON.parse(JSON.stringify(value));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const uuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const n = Math.floor(Math.random() * 16); return (c === 'x' ? n : (n & 3) | 8).toString(16);
});
const REQUEST = {ignoreModal: true, timeout: 20000};
// Also retain unacknowledged input for this tab when browser storage is denied.
const memoryBackups = new Map();
const sameTarget = (doc, current) => Number(doc.employee_id || 0) === Number(current.employee_id || 0) && doc.scope_key === current.scope_key;

// Personal editor only. Never registers records in the table's save queue.
export default class AssessmentDraft {
    constructor({api, url, key, storage, onState = () => {}, onApplied = () => {}, canSend = () => true,
        setTimer = (callback, delay) => setTimeout(callback, delay), clearTimer = timer => clearTimeout(timer)}) {
        Object.assign(this, {api, url, key, storage, onState, onApplied, canSend, setTimer, clearTimer});
        this.backupKey = `${key}:pending:${uuid()}`;
        this.status = 'loading'; this.error = ''; this.storageError = ''; this.dirty = false;
        this.recovery = null; this.conflict = null; this.stopped = false; this.readonly = false;
    }
    emit() { if (!this.stopped) this.onState(this); }
    newDoc(current) { return {draft_id: uuid(), revision: 0, employee_id: current.employee_id,
        scope_key: current.scope_key, baseline: clone(current), values: clone(current.values)}; }
    async load() {
        this.status = 'loading'; this.error = ''; this.emit();
        try {
            const {data} = await this.api.get(this.url, REQUEST);
            if (this.stopped) return;
            this.current = data.current; this.serverDraft = data.draft;
            this.doc = this.newDoc(data.current);
            const backups = [];
            try {
                if (this.storage) {
                    for (let i = 0; i < this.storage.length; i++) {
                        const key = this.storage.key(i);
                        if (key && key.startsWith(`${this.key}:pending:`)) {
                            try { const value = JSON.parse(this.storage.getItem(key)); if (value && value.doc) backups.push({key, ...value}); } catch (_) { /* Unrelated/corrupt recovery is never executed. */ }
                        }
                    }
                    const legacy = JSON.parse(this.storage.getItem(this.key));
                    if (legacy && legacy.draft) backups.push({key: this.key, legacy, savedAt: null});
                }
            } catch (_) { this.storageError = 'Браузер не предоставляет доступ к резервной копии. Серверный черновик доступен.'; }
            memoryBackups.forEach((value, key) => {
                if (key.startsWith(`${this.key}:pending:`)) {
                    const index = backups.findIndex(b => b.key === key);
                    if (index >= 0) backups.splice(index, 1);
                    backups.push({key, ...clone(value)});
                }
            });
            backups.forEach(backup => { backup.incompatible = backup.doc && !sameTarget(backup.doc, data.current); });
            this.recovery = data.draft || backups.length ? {server: data.draft, backups,
                incompatible: data.draft && !sameTarget(data.draft, data.current)} : null;
            this.status = 'ready'; this.emit();
        } catch (error) { this.failed(error); }
    }
    continueServer() {
        if (this.recovery.incompatible) return;
        this.doc = clone(this.recovery.server); this.recovery = null; this.status = 'saved'; this.emit();
    }
    restoreBackup(backup) {
        const saved = backup.legacy && backup.legacy.draft;
        const recovered = backup.doc;
        if (recovered && !sameTarget(recovered, this.current)) return;
        // Choosing recovery explicitly replaces this user's personal draft, never common data.
        this.doc = this.serverDraft ? clone(this.serverDraft) : this.newDoc(this.current);
        if (saved) {
            this.doc.values = {ktu: saved.ktu, ktu_evidence: saved.ktu_evidence || [], ktu_comment: saved.ktu_comment || '',
                characteristic_values: saved.characteristic_values || {}, copy_key: saved.copy_key || null, legacy_choice: saved.legacy_choice || null,
                review_required: true};
        } else if (recovered) {
            this.doc.values = clone(recovered.values);
            // A lost reply to apply must not silently replay an old assessment.
            if (!same(recovered.baseline, this.doc.baseline)) this.doc.values.review_required = true;
        }
        this.recoveredKey = backup.key; this.recovery = null; this.pending = null; this.conflict = null;
        this.dirty = true; this.backup(); this.emit(); this.schedule();
    }
    async resumeApplication(backup) {
        if (!backup.applyPending || !sameTarget(backup.doc, this.current)) return;
        this.doc = clone(backup.doc); this.applyPending = clone(backup.applyPending);
        this.recoveredKey = backup.key; this.recovery = this.conflict = this.pending = null;
        this.dirty = false;
        // Reuse the approved operation, including after reload; never publish a new
        // version just to discover whether the previous response was lost.
        return this.apply();
    }
    async resumeDiscard(backup) {
        if (!backup.discardPending || !sameTarget(backup.doc, this.current)) return;
        this.doc = clone(backup.doc); this.discardPending = true; this.recoveredKey = backup.key;
        this.recovery = null;
        return this.discard();
    }
    edit(value) {
        if (!this.doc || this.recovery || this.readonly || this.discarding || this.applyPending && !this.conflict || this.discardPending || this.status === 'applying') return;
        if (same(value, this.doc.values)) return;
        if (this.conflict && this.conflict.kind === 'values') { this.conflict = null; this.applyPending = null; }
        this.doc.values = clone(value); this.dirty = true;
        if (this.status === 'saved') this.status = 'ready';
        this.connectionAttempts = 0;
        this.backup(); this.emit(); this.schedule();
    }
    backup() {
        if (!this.dirty && !this.pending && !this.applyPending && !this.discardPending) return;
        const value = clone({doc: this.doc, pending: this.pending, applyPending: this.applyPending, discardPending: this.discardPending, savedAt: new Date().toISOString()});
        memoryBackups.set(this.backupKey, value);
        try {
            if (!this.storage) throw new Error('storage unavailable');
            this.storage.setItem(this.backupKey, JSON.stringify(value)); this.storageError = '';
        } catch (_) { this.storageError = 'Не удалось сохранить резервную копию на устройстве. Если сервер недоступен, не перезагружайте страницу: введённый текст останется только в открытой форме.'; }
    }
    removeBackup(key) { memoryBackups.delete(key); try { if (this.storage && key) this.storage.removeItem(key); } catch (_) { /* Saved server state remains authoritative. */ } }
    schedule() {
        this.cancelTimer();
        if (this.conflict || this.readonly || this.stopped || !this.canSend() || this.applyPending || this.discardPending) return;
        this.timer = this.setTimer(() => { this.timer = null; this.flush().catch(() => {}); }, 800);
    }
    cancelTimer() { if (this.timer) this.clearTimer(this.timer); this.timer = null; }
    failed(error) {
        const body = error.response && error.response.data || {};
        this.connectionError = !error.response || error.response.status >= 500;
        this.error = body.msg || body.message || 'Не удалось сохранить черновик на сервере. Ввод оставлен в форме. Повторите сохранение.';
        this.status = 'error';
        if (body.code === 'ASSESSMENT_DRAFT_CONFLICT' || body.code === 'ASSESSMENT_DRAFT_CLOSED')
            this.conflict = {kind: 'draft', ...body.details};
        if (body.code === 'ASSESSMENT_VALUES_CONFLICT') this.conflict = {kind: 'values', ...body.details};
        if ([400, 413, 422].includes(error.response && error.response.status)) this.pending = this.applyPending = null;
        if ([401, 403, 404].includes(error.response && error.response.status) || ['ASSESSMENT_DRAFT_TARGET_CHANGED', 'TIMESHEET_NOT_EDITABLE', 'TIMESHEET_PERIOD_LOCKED'].includes(body.code)) this.readonly = true;
        this.emit();
    }
    async flush() {
        this.cancelTimer();
        if (this.inFlight) { await this.inFlight; return this.flush(); }
        if (!this.doc || this.recovery || this.conflict || this.readonly || this.discarding || !this.dirty || this.stopped || !this.canSend() || this.applyPending || this.discardPending) return;
        if (!this.pending) this.pending = {draft_id: this.doc.draft_id, expected_revision: this.doc.revision,
            employee_id: this.doc.employee_id, scope_key: this.doc.scope_key, baseline: clone(this.doc.baseline),
            write_id: uuid(), values: clone(this.doc.values)};
        this.backup(); this.status = 'saving'; this.error = ''; this.emit();
        const pending = this.pending;
        this.inFlight = this.api.put(this.url, pending, REQUEST).then(({data}) => {
            this.doc.revision = data.draft.revision; this.doc.baseline = data.draft.baseline;
            this.serverDraft = data.draft; this.pending = null;
            this.connectionAttempts = 0;
            this.applyPending = null;
            this.dirty = !same(this.doc.values, pending.values);
            if (!this.dirty) { this.doc.values = clone(data.draft.values); this.removeBackup(this.backupKey); }
            this.removeBackup(this.recoveredKey); this.recoveredKey = null;
            this.status = this.discarding ? 'discarding' : this.dirty ? 'ready' : 'saved'; this.emit();
        }).catch(error => { this.failed(error); throw error; });
        try { await this.inFlight; } finally { this.inFlight = null; }
        if (this.dirty) return this.flush();
    }
    async apply(choices) {
        if (this.readonly || this.recovery || this.discarding || this.discardPending || this.stopped || !this.canSend() || this.status === 'applying') return;
        if (this.conflict && this.conflict.kind === 'values' && choices) {
            this.applyPending = {draft_id: this.doc.draft_id, expected_revision: this.doc.revision, operation_id: uuid(),
                choices, comparison_token: this.conflict.comparison_token};
            this.conflict = null;
        } else {
            await this.flush();
            if (this.conflict || this.dirty || !this.doc.revision || !this.canSend() || this.stopped) return;
            if (!this.applyPending) this.applyPending = {draft_id: this.doc.draft_id, expected_revision: this.doc.revision, operation_id: uuid()};
        }
        this.status = 'applying'; this.error = ''; this.backup(); this.emit();
        try {
            const {data} = await this.api.post(`${this.url}/apply`, this.applyPending, REQUEST);
            this.applyPending = null; this.removeBackup(this.backupKey); this.removeBackup(this.recoveredKey);
            this.dirty = false; this.status = 'applied'; this.emit(); if (!this.stopped) this.onApplied(data.result);
        } catch (error) { this.failed(error); throw error; }
    }
    async discard() {
        if (this.discarding || this.readonly || this.stopped || !this.canSend()) return;
        this.discarding = true; this.status = 'discarding'; this.emit();
        this.cancelTimer();
        if (this.inFlight) { try { await this.inFlight; } catch (_) { /* Fetch current revision before explicit discard. */ } }
        this.status = 'discarding'; this.emit();
        try {
            const {data} = await this.api.get(this.url, REQUEST);
            const row = this.recovery ? this.recovery.server : this.doc;
            if (this.stopped) return;
            if (this.readonly || !this.canSend()) throw new Error('Удаление приостановлено');
            if (data.draft) {
                if (!row || row.draft_id !== data.draft.draft_id || row.revision !== data.draft.revision) {
                    this.conflict = {kind: 'draft', ...data}; this.status = 'error';
                    this.error = 'Черновик изменился. Просмотрите актуальный вариант перед удалением.'; this.emit(); return;
                }
                this.discardPending = true; this.backup();
                await this.api.delete(this.url, {...REQUEST, data: {draft_id: row.draft_id, expected_revision: row.revision}});
            } else if (row && row.revision === 0) {
                this.discardPending = true; this.backup();
                await this.api.delete(this.url, {...REQUEST, data: {draft_id: row.draft_id, expected_revision: 0}});
            }
            if (this.recovery) {
                for (const backup of this.recovery.backups) {
                    if (backup.doc && backup.doc.revision === 0 && (!row || backup.doc.draft_id !== row.draft_id))
                        await this.api.delete(this.url, {...REQUEST, data: {draft_id: backup.doc.draft_id, expected_revision: 0}});
                }
                this.recovery.backups.forEach(b => this.removeBackup(b.key));
            }
            this.removeBackup(this.backupKey); this.removeBackup(this.recoveredKey);
            this.doc = this.newDoc(data.current); this.current = data.current; this.serverDraft = null;
            this.pending = this.applyPending = this.recovery = this.conflict = null; this.discardPending = false;
            this.dirty = false; this.status = 'ready'; this.error = ''; this.emit();
        } catch (error) { this.failed(error); throw error; }
        finally { this.discarding = false; }
    }
    async resolveDraft(useMine) {
        if (!this.canSend() || this.readonly || this.stopped) return;
        const {data} = await this.api.get(this.url, REQUEST);
        const displayed = this.conflict && this.conflict.draft;
        if (!same(data.draft && [data.draft.draft_id, data.draft.revision], displayed && [displayed.draft_id, displayed.revision])) {
            this.conflict = {kind: 'draft', ...data}; this.error = 'Черновик снова изменился. Проверьте актуальный вариант.'; this.emit(); return;
        }
        const mine = clone(this.doc.values);
        this.doc = data.draft ? clone(data.draft) : this.newDoc(data.current);
        this.conflict = this.pending = this.applyPending = this.recovery = null; this.discardPending = false; this.error = ''; this.dirty = false;
        this.status = data.draft ? 'saved' : 'ready';
        if (useMine) { mine.review_required = true; this.edit(mine); await this.flush(); }
        else this.removeBackup(this.backupKey);
        this.emit();
    }
    pauseConnection() { this.cancelTimer(); this.backup(); }
    async resumeConnection() {
        if (this.connectionFlight || (this.connectionAttempts || 0) >= 3 || this.applyPending || this.discardPending) return;
        this.connectionFlight = true;
        try {
            if (this.inFlight) { try { await this.inFlight; } catch (_) { /* Retry the same unacknowledged write. */ } }
            if (this.stopped || !this.canSend() || this.readonly || this.conflict || this.recovery || !this.dirty) return;
            this.connectionAttempts = (this.connectionAttempts || 0) + 1;
            await this.flush();
        } finally { this.connectionFlight = false; }
    }
    close() { this.cancelTimer(); this.backup(); if (!this.conflict) this.flush().catch(() => {}); }
    stop() { this.close(); this.stopped = true; }
}
